"use client";

import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useStoreSlug } from "@/hooks/use-store-slug";
import { useStores } from "@/hooks/use-stores";
import { getOrdersAdapter } from "../adapters/get-orders.adapter";

/**
 * Shared query definition reused by the route loader and the hook so both touch
 * the same cache entry (key + fn identical).
 */
export function ordersQueryOptions(
	storeSlug: string | undefined,
	storeId: string | undefined,
) {
	return {
		queryKey: ["orders", storeSlug, storeId] as const,
		queryFn: () => getOrdersAdapter(storeId),
	};
}

export function useOrders() {
	const storeSlug = useStoreSlug();
	const { stores } = useStores();

	const currentStore = React.useMemo(() => {
		if (!storeSlug) return null;
		return stores.find((store) => store.slug === storeSlug) || null;
	}, [storeSlug, stores]);

	const {
		data: orders = [],
		isLoading,
		error,
	} = useQuery({
		...ordersQueryOptions(storeSlug, currentStore?.id),
		enabled: !!currentStore,
	});

	return {
		orders,
		isLoading,
		error,
	};
}
